import { useState, useRef } from 'react';
import { Hero } from '../components/Hero';
import { Button } from '../components/Button';
import bookCover from '../assets/NOB_KINDLE_COVER.jpg';
import './Book.css';

const reviews = [
  {
    username: 'SecondDerivative',
    format: 'Paperback',
    date: 'February 20, 2026',
    title: 'Subtle and evocative — great read',
    body: 'Gave the book a quick read, then went back and read selected chapters very carefully. An evocative book that had me looking back in time at my interpretations of life, and revealed pieces of my true self. I am left to cogitate on meaning and purpose of things from a more clear viewpoint.',
  },
  {
    username: 'TexasCowGirl',
    format: 'Hardcover',
    date: 'February 21, 2026',
    title: 'An adventure in who we think we are',
    body: 'This is a rigorous intellectual adventure that discusses the difference between observing your surroundings and understanding them. A witty challenge to the very notion of who we think we are, arguing that much of our identity is actually an illusion built by symbolic programming.',
  },
  {
    username: 'Glacier',
    format: 'Paperback',
    date: 'February 27, 2026',
    title: 'Interesting perspective on seeing the world around us',
    body: 'This book provided interesting insight on seeing the world around us and how symbols have been used to increase efficiency resulting in compressing our role in making decisions. But we still need to understand the symbols and systems around us. The book encourages us to not surrender agency, but to open our eyes and be more thoughtful about the consequences of our choices, and not let compression or the definition of what is normal become the scapegoat for our decisions.',
  },
];

export function Book() {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const prev = () => setActiveIndex(i => (i - 1 + reviews.length) % reviews.length);
  const next = () => setActiveIndex(i => (i + 1) % reviews.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) delta < 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <div className="book-page">
      <Hero
        title="The Book"
        subtitle="How humans write reality to survive it"
        size="small"
      />

      <section className="book-page__main">
        <div className="container">
          <div className="book-page__content">
            <div className="book-page__cover">
              <img src={bookCover} alt="Notation of Being book cover" className="book-page__cover-img" />
            </div>

            <div className="book-page__details">
              <h2>Notation of Being</h2>

              <div className="book-page__description">
                <p>
                  <em>Notation of Being</em> explores how humans do not experience reality directly,
                  but through symbolic systems that compress complexity into something livable.
                </p>
                <p className="book-page__concepts">
                  Time. Money. Identity. Productivity. Tradition.<br />
                  What feels "normal." What feels inevitable.
                </p>
                <p>
                  These are not laws of nature. They are human-made notations—models, stories,
                  and abstractions that quietly shape how we perceive ourselves, judge our lives,
                  and decide what matters.
                </p>
                <p>
                  Rather than arguing against structure, belief, or meaning, <em>Notation of Being</em> examines
                  why these systems exist, how they harden over time, and what becomes possible
                  when we learn to see them clearly. The book moves from the necessity of symbolic
                  compression, through inherited ontologies and frozen meaning, and finally toward
                  a way of living consciously within symbols without collapsing into nihilism or dogma.
                </p>
                <p className="book-page__closing-thought">
                  This is not a call to escape the world.<br />
                  It is an invitation to understand the symbolic world you already inhabit—and to
                  participate in writing it with greater awareness and responsibility.
                </p>
              </div>

              <div className="book-page__cta">
                <h3>Buy the Book</h3>
                <div className="book-page__cta-buttons">
                  <Button
                    href="https://www.amazon.com/dp/B0GNTY4RLD"
                    size="large"
                  >
                    <i className="fab fa-amazon"></i>
                    Hardcover
                  </Button>
                  <Button
                    href="https://www.amazon.com/dp/B0GNVQRD9X"
                    size="large"
                  >
                    <i className="fab fa-amazon"></i>
                    Paperback
                  </Button>
                  <Button
                    href="https://www.amazon.com/dp/B0GNTC5WVD"
                    size="large"
                  >
                    <i className="fab fa-amazon"></i>
                    Kindle
                  </Button>
                  <Button variant="outline" size="large" disabled>
                    <i className="fas fa-podcast"></i>
                    Discussions Coming Soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="book-page__excerpt">
        <div className="container container--narrow">
          <h2 className="text-center">From the Book</h2>
          <blockquote className="book-page__quote">
            <p>
              "The most powerful systems are the ones we do not experience as systems at all.
              They feel like reality itself.
              Notation of Being is not about dismantling those structures—but about learning
              when they are serving us, and when they have begun deciding on our behalf."
            </p>
            <cite>— Notation of Being</cite>
          </blockquote>
        </div>
      </section>

      <section className="book-page__features">
        <div className="container">
          <h2 className="text-center">What You'll Discover</h2>
          <div className="book-page__features-grid">
            <div className="book-page__feature">
              <div className="book-page__feature-icon">
                <i className="fas fa-layer-group"></i>
              </div>
              <h3>Symbolic Systems You Live Inside</h3>
              <p>
                Understand how everyday abstractions—time, value, identity, productivity,
                and "normal"—quietly organize perception and behavior long before conscious
                choice enters the picture.
              </p>
            </div>
            <div className="book-page__feature">
              <div className="book-page__feature-icon">
                <i className="fas fa-snowflake"></i>
              </div>
              <h3>Why Meaning Freezes—and Why That's Dangerous</h3>
              <p>
                Explore how living symbols harden into institutions, policies, and identities,
                and why reform so often feels like heresy rather than healing.
              </p>
            </div>
            <div className="book-page__feature">
              <div className="book-page__feature-icon">
                <i className="fas fa-compass"></i>
              </div>
              <h3>Ethics Without Outsourcing</h3>
              <p>
                Learn what responsibility looks like when rules stop deciding for you—how
                ethics shifts from obedience to presence, authorship, and compassion under uncertainty.
              </p>
            </div>
            <div className="book-page__feature">
              <div className="book-page__feature-icon">
                <i className="fas fa-infinity"></i>
              </div>
              <h3>How to Live Symbolically Without Losing Meaning</h3>
              <p>
                Discover how awareness does not dissolve meaning, but deepens it—allowing
                you to hold tradition, belief, and structure consciously rather than literally.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="book-page__audience">
        <div className="container container--narrow">
          <h2>Who This Book Is For</h2>
          <ul className="book-page__audience-list">
            <li>
              <i className="fas fa-check"></i>
              <span>Readers who sense that modern life feels over-structured but don't want cynicism or nihilism</span>
            </li>
            <li>
              <i className="fas fa-check"></i>
              <span>Thinkers, builders, parents, leaders, and creators who live inside systems and want to understand them</span>
            </li>
            <li>
              <i className="fas fa-check"></i>
              <span>Anyone who has felt the quiet pressure of "normal" and wondered where it came from</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="book-page__not">
        <div className="container container--narrow">
          <div className="book-page__not-grid">
            <div className="book-page__not-section">
              <h2>What This Book Is Not</h2>
              <ul>
                <li>Not a meditation manual</li>
                <li>Not a self-help checklist</li>
                <li>Not a rejection of belief, tradition, or structure</li>
                <li>Not an escape from responsibility</li>
              </ul>
            </div>
            <div className="book-page__is-section">
              <h2>What It Is</h2>
              <p>
                A guide to symbolic literacy.<br />
                A map of the water we swim in.<br />
                And an invitation to write more consciously inside the systems that make life possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="book-page__reviews">
        <div className="container container--narrow">
          <h2 className="text-center">Reader Reviews</h2>

          <div className="book-page__reviews-summary">
            <div className="book-page__reviews-score">5.0</div>
            <div className="book-page__reviews-meta">
              <div className="book-page__stars">
                {[1,2,3,4,5].map(n => (
                  <i key={n} className="fas fa-star"></i>
                ))}
              </div>
              <span className="book-page__reviews-count">3 verified ratings on Amazon</span>
              <span className="book-page__reviews-updated">Last updated Feb 27, 2026</span>
            </div>
          </div>

          <div className="book-page__carousel">
            <button className="book-page__carousel-btn book-page__carousel-btn--prev" onClick={prev} aria-label="Previous review">
              <i className="fas fa-chevron-left"></i>
            </button>

            <div
              className="book-page__carousel-track"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              {reviews.map((review, i) => (
                <div
                  key={i}
                  className={`book-page__review-card${i === activeIndex ? ' book-page__review-card--active' : ''}`}
                  aria-hidden={i !== activeIndex}
                >
                  <div className="book-page__review-header">
                    <div className="book-page__review-stars">
                      {[1,2,3,4,5].map(n => <i key={n} className="fas fa-star"></i>)}
                    </div>
                    <span className="book-page__review-verified">
                      <i className="fas fa-check-circle"></i> Verified Purchase
                    </span>
                  </div>
                  <h3 className="book-page__review-title">{review.title}</h3>
                  <p className="book-page__review-byline">
                    <strong>{review.username}</strong> &middot; {review.format} &middot; {review.date}
                  </p>
                  <p className="book-page__review-body">{review.body}</p>
                </div>
              ))}
            </div>

            <button className="book-page__carousel-btn book-page__carousel-btn--next" onClick={next} aria-label="Next review">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          <div className="book-page__carousel-dots">
            {reviews.map((_, i) => (
              <button
                key={i}
                className={`book-page__carousel-dot${i === activeIndex ? ' book-page__carousel-dot--active' : ''}`}
                onClick={() => setActiveIndex(i)}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>

          <div className="book-page__reviews-cta">
            <a
              href="https://www.amazon.com/dp/B0GNTY4RLD#customerReviews"
              target="_blank"
              rel="noopener noreferrer"
              className="book-page__reviews-link"
            >
              <i className="fab fa-amazon"></i>
              See all reviews on Amazon
              <i className="fas fa-arrow-right"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
